<script setup lang="ts">
import FinestCountBadge from '@/components/FinestCountBadge.vue'
import { initials } from '@/utils/initials'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    label: string
    sublabel?: string
    selected?: boolean
    count?: number
    countLabel?: string
    icon?: string
    avatarGradient?: boolean
  }>(),
  {
    sublabel: '',
    selected: false,
    count: 0,
    countLabel: undefined,
    icon: undefined,
    avatarGradient: false,
  },
)

const avatarInitials = computed(() => initials(props.label))

/** The brand gradient marks the entry as the signed-in user, so it outranks the selected state. */
const avatarClass = computed(() => {
  if (props.avatarGradient) {
    return 'text-white'
  }

  return props.selected ? 'bg-action text-white' : 'bg-surface-3 text-ink-2'
})
</script>

<template>
  <button
    type="button"
    class="flex cursor-pointer items-center gap-2.5 rounded-[10px] border px-3 py-2.5 text-left transition-colors"
    :class="selected ? 'border-action bg-info-subtle' : 'border-line hover:border-action/50'">
    <span
      class="flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11px] font-bold"
      :class="avatarClass"
      :style="avatarGradient ? { background: 'var(--gradient-brand)' } : undefined">
      <i v-if="icon" :class="icon" class="text-xs" aria-hidden="true" />
      <template v-else>{{ avatarInitials }}</template>
    </span>

    <span class="min-w-0 flex-1">
      <span class="block truncate text-sm font-medium">{{ label }}</span>
      <span v-if="sublabel" class="text-ink-3 block truncate text-xs tabular-nums">{{ sublabel }}</span>
    </span>

    <FinestCountBadge :count="count" :aria-label="countLabel" class="flex-none" />

    <i v-if="selected" class="pi pi-chevron-right text-action flex-none text-xs" aria-hidden="true" />
  </button>
</template>
